import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  FormField,
  Header,
  Modal,
  RadioGroup,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';

import ScanInput from '../ScanInput';
import { SHIPMENT_MISSING_REASON_CODES } from './constants';

interface IsRMATypeProps {
  onDismiss: () => void;
  onConfirm: () => void;
  rma: string;
  isVisible?: boolean;
}

interface ConfirmRMAIDProps {
  onDismiss: () => void;
  onConfirm: () => void;
  rma: string;
  setRma: (val: boolean) => void;
}

interface ShipmentMissingReasonProps {
  reason: string;
  setReason: (val: string) => void;
}

interface ShipmentMissingReasonModalProps {
  selected: any;
  onDismiss: () => void;
  onConfirm: (shipmentMissingReasonCode: string) => void;
}

export const IsRMAType = ({ onDismiss, onConfirm, rma, isVisible = true }: IsRMATypeProps) => {
  const [visible, setVisible] = useState(isVisible);
  const okBtnRef = useRef(null);

  const onHandleConfirmRMA = () => {
    setVisible(false);
    onConfirm && onConfirm();
  };

  useEffect(() => {
    setTimeout(() => {
      okBtnRef && okBtnRef?.current?.focus();
    }, 0);
  }, []);

  return (
    <Modal
      onDismiss={() => setVisible(false)}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              ariaLabel="reject rma type"
              onClick={() => {
                setVisible(false);
                onDismiss();
              }}
            >
              No
            </Button>
            <Button
              ref={okBtnRef}
              variant="primary"
              tab-index="1"
              ariaLabel="confirm rma type"
              onClick={onHandleConfirmRMA}
            >
              Yes
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={`Reference not found for ${rma}`}
    >
      <Box>
        <Box margin={{ bottom: 's' }}>
          <Alert statusIconAriaLabel="Error" type="error">
            The entered value is not a valid shipment ID or PO. Is this an RMA ID?
          </Alert>
        </Box>
        <Box>Receive as RMA (Replacement Parts Shipped From Vendor) ?</Box>
      </Box>
    </Modal>
  );
};

export const ConfirmRMAID = ({ setRma, onDismiss, onConfirm, rma }: ConfirmRMAIDProps) => {
  const [visible, setVisible] = useState<boolean>(true);
  const [renteredRMA, setRenteredRMA] = useState<string>('');

  const onHandleConfirmRMA = () => {
    if (renteredRMA.trim().length === 0 || renteredRMA.trim() !== rma.trim()) {
      return;
    }
    setVisible(false);
    setRma(true);
    onConfirm && onConfirm();
  };
  return (
    <Modal
      onDismiss={() => {
        setVisible(false);
        onDismiss();
      }}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              ariaLabel="reject re entered rma type"
              onClick={() => {
                setVisible(false);
                setRma(false);
                onDismiss();
              }}
            >
              No
            </Button>
            <Button
              variant="primary"
              ariaLabel="confirm re entered rma type"
              disabled={renteredRMA.trim().length === 0 || renteredRMA.trim() !== rma.trim()}
              onClick={onHandleConfirmRMA}
            >
              Yes
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Rescan RMA Id"
    >
      <Box>
        <FormField
          label="RMA ID"
          description=" Be sure to enter only RMA ID and these entries will be audited periodically"
        >
          <SpaceBetween direction="vertical" size="m">
            <ScanInput
              onScan={onHandleConfirmRMA}
              ariaLabel="reenter-rma-id"
              onChange={(value) => setRenteredRMA(value)}
              value={renteredRMA}
              placeholder="Scan RMA Id"
            />

            {renteredRMA !== rma && (
              <Alert statusIconAriaLabel="Error" type="error">
                The RMA ID entered does not match the previous input. Please re-enter the value to proceed.
              </Alert>
            )}
          </SpaceBetween>
        </FormField>
      </Box>
    </Modal>
  );
};

export const ShipmentMissingReason = ({ reason, setReason }: ShipmentMissingReasonProps) => {
  return (
    <RadioGroup
      onChange={({ detail }) => setReason(detail.value)}
      value={reason}
      items={SHIPMENT_MISSING_REASON_CODES}
    />
  );
};

export const ShipmentMissingReasonModal = ({ onConfirm, onDismiss, selected }: ShipmentMissingReasonModalProps) => {
  const [visible] = useState(true);
  const [reason, setReason] = useState(selected?.value || 'shipment_missing');

  return (
    <Modal
      onDismiss={onDismiss}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              disabled={!reason}
              variant="primary"
              onClick={() => {
                onConfirm(reason);
              }}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={
        <Header variant="h3" description="If you wish to receive against a PO? Choose one of the below reason.">
          You entered a PO
        </Header>
      }
    >
      <ShipmentMissingReason reason={reason} setReason={setReason} />
    </Modal>
  );
};
